import { inject, Injectable } from '@angular/core';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { AccessRightType, ApplicationSettingsType, ObjectTypes } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiQuizService } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz.service';
import { IQuizItemBase, IQuizQuestion } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiQuizHelperService } from './fi-quiz-helper.service';
import { getObjectTypeNameTextId } from '@fi-shared/utils';

interface IBuildQuizActionsArgs<T> {
    onQuizDeletedFn: (item: T) => void;
}

@Injectable()
export class FiQuizActionsBuilderService {
    private fiQuizHelperService = inject(FiQuizHelperService);

    constructor(
        private appTextService: AppTextService,
        private fiStateService: FiStateService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiOnboardingHelperService: FiOnboardingHelperService,
        private fiQuizService: FiQuizService,
    ) {}

    getQuizActions<T extends IQuizItemBase>(args: IBuildQuizActionsArgs<T>): IItemsListAction2<T>[] {
        const hasShareRights = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.OnboardingLocal,
            AccessRightType.Share,
        );
        const quizTextId = getObjectTypeNameTextId(ObjectTypes.Quiz);
        const quizText = this.appTextService.get(quizTextId).toLowerCase();

        const actions: IItemsListAction2<T>[] = [
            {
                title: this.appTextService.get('common.edit') + ' ' + quizText,
                condition: (item) => hasShareRights && item.HasEditAccess,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-pen-to-square',
                onClick: (item) => this.fiStateService.go(FiStateNames.Onboarding.Quiz.Edit, { id: item.Id }),
            },
            {
                title: this.appTextService.get('copy') + ' ' + quizText,
                condition: (item) => hasShareRights,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-copy',
                onClick: (item) => this.fiStateService.go(FiStateNames.Onboarding.Quiz.Copy, { id: item.Id }),
            },
            {
                title: this.appTextService.get('common.delete') + ' ' + quizText,
                condition: (item) => hasShareRights && item.HasEditAccess,
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-trash',
                onClick: (item) => {
                    this.fiOnboardingHelperService.showStepElementDeletingConfirmationDialog({
                        stepElementObjectId: item.Id,
                        stepElementObjectType: ObjectTypes.Quiz,
                        stepElementName: item.Name,
                        deleteFn: () => {
                            this.fiQuizService.deleteQuizzes([item.Id]).then(() => {
                                args.onQuizDeletedFn(item);
                            });
                        },
                    });
                },
            }
        ];

        return actions;
    }

    getQuizQuestionActions(): IItemsListAction2<IQuizQuestion>[] {
        return [
            {
                title: this.appTextService.get('common.delete'),
                optionType: FiItemsListOptionType.Icon,
                iconClass: 'fa-regular fa-trash',
                onClick: (item) => this.fiQuizHelperService.confirmDeleteQuizQuestion(item),
            },
        ];
    }
}
